import { autoinject } from "aurelia-dependency-injection";
import { bindable, bindingMode, BindingEngine, customElement, Disposable } from "aurelia-framework";

import { EnumMap } from "../../../common/utilities/enum-map";
import { IEnumResponse } from "../../../interfaces/i-enum";
import { EnumsService } from "../../../services/enums-service";
import { PatientsService } from "../../../services/patient-service";
import { NewService } from "../../../models/new-service";
import { AccountsService } from "../../../services/accounts-service";
import { IValidateCustomElement } from "../../../interfaces/i-validate-custom-element";
import { formatName } from "../../../common/utilities/format-name";
import { UsersService } from "../../../services/users-service";
import { IContact, IGetBereavedLeanResult } from "../../../interfaces/i-bereavement-poc";
import { BereavementPOCService } from "../../../services/bereavement-poc-service";

@autoinject
@customElement("add-service-form")
export class AddServiceForm {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public newService: NewService = null;
    public cachedService: NewService = new NewService();
    private readonly _enumsService: EnumsService;
    private readonly _usersService: UsersService;
    private readonly _patientsService: PatientsService;
    private readonly _bereavementPocService: BereavementPOCService;
    private readonly _accountsService: AccountsService;
    private _bindingEngine: BindingEngine;
    private _Subscription: Disposable[] = [];
    public payerTypes: IEnumResponse[] = [];
    public payerTypeEnum: IEnumResponse[] = [];
    public payerTypeEnumMap: EnumMap = new EnumMap([]);
    public isError: boolean = false;
    public serviceOptionsEnum: IEnumResponse[] = [];
    public serviceOptionsEnumMap: EnumMap = new EnumMap([]);
    public contacts: IContact[] = [];
    public bereavedContactsList: IGetBereavedLeanResult[] = [];
    public patientSubscription: any;
    public typeValidation: boolean = false;
    public InValid: boolean = false;
    public patientIdChanged: boolean = false;
    public isPatientSelected: boolean = false;
    public serviceValidation: IValidateCustomElement = {
        required: true,
        message: "Bereavement Service is required."
    };
    public contactValidation: IValidateCustomElement = {
        required: true,
        message: "Bereaved is required."
    };

    public patientValidation: IValidateCustomElement = {
        required: true,
        message: "Patient is required."
    };

    public constructor(
        enumsService: EnumsService,
        patientsService: PatientsService,
        accountsService: AccountsService,
        usersService: UsersService,
        bindingEngine: BindingEngine,
        bereavementPocService: BereavementPOCService
    ) {
        this._enumsService = enumsService;
        this._patientsService = patientsService;
        this._accountsService = accountsService;
        this._bindingEngine = bindingEngine;
        this._usersService = usersService;
        this._bereavementPocService = bereavementPocService;
    }

    public async attached() {
        this.cachedService = new NewService();
        this.addObserver();
        this.payerTypeEnum = await this._enumsService.getPayorType();
        this.payerTypeEnumMap = new EnumMap(this.payerTypeEnum);
        this.getServiceOptionEnum();
    }

    public validateContact(resetValidation = false) {
        this.InValid = this.cachedService.bereavedContacts.length === 0 && !resetValidation;
    }

    public setIsPatientSelected(isPatientSelected: boolean = false) {
        this.isPatientSelected = isPatientSelected;
    }

    public addObserver() {
        const properties = ["date", "assignedTo"];
        properties.forEach((props) => {
            this._Subscription.push(
                this._bindingEngine.propertyObserver(this.cachedService, props).subscribe(() => {
                    this.newService = this.cachedService;
                })
            );
        });

        this._Subscription.push(
            this._bindingEngine
                .propertyObserver(this.cachedService, "patient")
                .subscribe((newValue: any, oldValue: any) => {
                    this.newService = this.cachedService;
                    this.setIsPatientSelected(!!this.newService.patient?.value);
                    this.patientIdChanged = !this.patientIdChanged;
                    if (!this.isPatientSelected) {
                        this.InValid = false;
                        this.cachedService.bereavedContacts = [];
                        this.cachedService.bereavementService = [];
                        this.cachedService.assignedTo = null;
                    }
                })
        );

        this._Subscription.push(
            this._bindingEngine
                .collectionObserver(this.cachedService.bereavedContacts)
                .subscribe((newBereavedContact) => {
                    this.newService = this.cachedService;
                    this.InValid = this.newService.bereavedContacts.length === 0;
                })
        );

        this._Subscription.push(
            this._bindingEngine.collectionObserver(this.cachedService.bereavementService).subscribe(() => {
                this.cachedService.bereavementService = [...this.cachedService.bereavementService];
                this.typeValidation = this.typeValidation ? true : false;
                this.newService = this.cachedService;
            })
        );
    }

    public async patientFetch(term: string) {
        let currentLineOfService = await this._accountsService.getLineOfService();
        let params = {
            page: 0,
            pageLength: 50,
            term: term,
            lineOfService: currentLineOfService
        };
        const patientData = await this._patientsService.getPatientsLean(params);
        return patientData.items.map((p) => ({ name: `${p.lastName}, ${p.firstName}`, value: p.id }));
    }

    public async usersFetch(filter: string) {
        let users = await this.getUsersList(filter);
        if (!!users) {
            return users.items.map((user) => ({
                name: formatName(user.firstName, user.lastName),
                value: user.id
            }));
        }
        return [];
    }

    public async contactsFetch(filter: string) {
        let patientId = this.cachedService?.patient?.value;
        try {
            if (!!patientId) {
                const contactList = await this.getContactsListOfPatient(patientId, filter);
                return contactList.map((b) => ({ name: b.fullName, value: b.id }));
            }

            return [];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    private async getServiceOptionEnum() {
        this.serviceOptionsEnum = await this._enumsService.getServiceOptions();
        this.serviceOptionsEnumMap = new EnumMap(this.serviceOptionsEnum);
    }

    private async getUsersList(term: string) {
        return await this._usersService.getUsersLean({
            status: 1,
            page: 1,
            pageLength: 50,
            term: term
        });
    }

    private async getContactsListOfPatient(patientId: string, filter: string) {
        return await this._bereavementPocService.getBereavedContactsLean({
            patientId,
            term: filter ?? ""
        });
    }

    public detached() {
        this._Subscription.forEach((s) => s.dispose);
    }
}
